import { useState, useEffect, useCallback } from 'react';
import { Pencil, Plus, X, GripVertical, Download } from 'lucide-react';
import { color, mono } from '../tokens';
import { type JourneyStage } from '../fixtures';
import { useSynth, useSynthNav, useSynthProjectId } from '../SynthesisContext';
import { useOverlays } from '../useOverlays';
import { asText } from '../asText';
import ProposalModal from '../components/ProposalModal';
import { countOf } from '../../../utils/pluralize';
import { moodEmoji, normalizeSentiment, sentimentColor } from '../sentiment';
import { exportJourneyPptx } from '../exportJourneyPptx';

// Consultant journey-stage edits are persisted to the viz-owned overlay store under this reserved
// key (NOT an entity id), with a distinct type so the Mind Map hydrator — which only reads
// overlays where type === 'merge' — never picks it up. Reusing the existing overlays endpoint keeps
// this additive (no new table / migration). Overlay wins over the proposed journey on load, so
// hand-curation survives a reload or a re-run of synthesis.
const JOURNEY_OVERLAY_KEY = '__journey_stages__';

interface JourneyProps {
  // Driven by the shell so the first-render gate fires ONCE per session, not on
  // every tab switch. Absent (undefined) => treat as already seen (e.g. previews).
  proposalSeen?: boolean;
  onConfirmProposal?: () => void;
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.08em', color: color.textFaint, marginBottom: 4 }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function StageCard({ s, open, onToggle, onOpenTheme }: {
  s: JourneyStage; open: boolean; onToggle: () => void; onOpenTheme: (rank: number) => void;
}) {
  const sent = normalizeSentiment(s.sentiment);
  const sc = sentimentColor(sent);
  return (
    <div style={{
      flex: '0 0 260px', width: 260, background: color.surface,
      border: `1px solid ${color.hairline}`, borderRadius: 12, overflow: 'hidden',
    }}>
      <div style={{ height: 4, background: s.color }} />
      <button onClick={onToggle} style={{
        width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: '14px 16px 10px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: color.ink, flex: 1, minWidth: 0 }}>{s.name}</span>
          <span style={{ fontSize: 11, fontFamily: mono, color: color.textFaint }}>{s.duration}</span>
          <span style={{ fontSize: 14, color: color.textMuted }}>{open ? '−' : '+'}</span>
        </div>
        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ flex: 1, height: 6, borderRadius: 999, background: '#F1F5F9', overflow: 'hidden' }}>
            <div style={{ width: `${Math.round(sent * 100)}%`, height: '100%', background: sc }} />
          </div>
          <span style={{ fontSize: 11, fontFamily: mono, color: sc, fontWeight: 700 }}>
            {Math.round(sent * 100)}%
          </span>
        </div>
      </button>
      <div style={{ padding: '0 16px 16px' }}>
        <Section label="TEAM">
          <p style={{ margin: 0, fontSize: 12, color: color.textMuted, lineHeight: 1.5 }}>{asText(s.team) || '—'}</p>
        </Section>
        <Section label="ACTIONS">
          <p style={{ margin: 0, fontSize: 12, color: color.textMuted, lineHeight: 1.5 }}>{asText(s.actions) || '—'}</p>
        </Section>
        <Section label="TOUCHPOINTS">
          <p style={{ margin: 0, fontSize: 12, color: color.textMuted, lineHeight: 1.5 }}>{asText(s.touch) || '—'}</p>
        </Section>
        <Section label="EXPERIENCE">
          <p style={{ margin: 0, fontSize: 12, color: color.text, lineHeight: 1.5 }}>
            <span style={{ marginRight: 6 }}>{moodEmoji(sent)}</span>
            {asText(s.exp) || '—'}
          </p>
        </Section>
        <Section label="KPIS">
          <p style={{ margin: 0, fontSize: 12, color: color.textMuted, lineHeight: 1.5 }}>{asText(s.kpi) || '—'}</p>
        </Section>

        {open && (
          <>
            <Section label="GOALS">
              <p style={{ margin: 0, fontSize: 12, color: color.text, lineHeight: 1.5 }}>{asText(s.goals) || '—'}</p>
            </Section>
            {s.moments?.length > 0 && (
              <Section label="MOMENTS THAT MATTER">
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  {s.moments.map((m, i) => (
                    <li key={i} style={{ fontSize: 12, color: color.text, lineHeight: 1.5 }}>{asText(m)}</li>
                  ))}
                </ul>
              </Section>
            )}
            {s.pains?.length > 0 && (
              <Section label="PAIN POINTS">
                {s.pains.map((p, i) => (
                  <div key={i} style={{
                    fontSize: 12, color: '#B91C1C', background: '#FEE2E2',
                    borderRadius: 6, padding: '5px 8px', marginBottom: 5, lineHeight: 1.4,
                  }}>{asText(p)}</div>
                ))}
              </Section>
            )}
            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, fontFamily: mono, color: color.textMuted }}>{countOf(s.quotes, 'quote')}</span>
              {(s.themes || []).map((t) => (
                <button key={t} onClick={() => onOpenTheme(t)} style={{
                  fontSize: 11, fontFamily: mono, color: color.purpleDeep, background: color.pillActiveBg,
                  border: `1px solid ${color.chipBorder}`, borderRadius: 6, padding: '2px 7px', cursor: 'pointer',
                }}>T{String(t).padStart(2, '0')}</button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// A blank stage the consultant can fill in — corpus grows during an engagement, so
// adding a stage must be first-class, not "start the project over".
function blankStage(): JourneyStage {
  return {
    name: 'New stage', color: '#7C3AED', actions: '', touch: '', exp: '', kpi: '',
    team: '', sentiment: 0.5, quotes: 0, duration: '', goals: '',
    moments: [], pains: [], themes: [],
  };
}

export default function Journey({ proposalSeen, onConfirmProposal }: JourneyProps) {
  const { journey: proposedJourney, themes, engagement } = useSynth();
  const { setActive, setExpandedThemeRank } = useSynthNav();
  const projectId = useSynthProjectId();
  const { overlays, loaded, editable, saveOverlay } = useOverlays(projectId);
  const [stages, setStages] = useState<JourneyStage[]>(() => proposedJourney);
  const [editMode, setEditMode] = useState(false);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [hydrated, setHydrated] = useState(false);
  const [openStages, setOpenStages] = useState<Record<number, boolean>>({});
  const [exporting, setExporting] = useState(false);

  const showModal = proposalSeen === false;

  // Hydrate saved stages from the overlay once it loads; the overlay wins over the proposed
  // journey so hand-curation persists across reloads / re-runs. Runs once (overlays load once
  // per mount), so it never clobbers in-session edits.
  useEffect(() => {
    if (!loaded || hydrated) return;
    const saved = overlays.find(
      (o) => o.entity_id === JOURNEY_OVERLAY_KEY && o.overlay?.type === 'journey' && Array.isArray(o.overlay.stages),
    );
    if (saved) setStages(saved.overlay.stages as JourneyStage[]);
    setHydrated(true);
  }, [loaded, hydrated, overlays]);

  // Persist the whole stage list to the overlay. Structural edits (add/remove/reorder) call this
  // immediately and renames on blur, so edits survive a tab switch or reload — never a silent loss.
  const persist = useCallback(async (next: JourneyStage[]) => {
    if (!editable) return;               // no project → local-only, like the roster editor
    setSaveState('saving');
    const ok = await saveOverlay(JOURNEY_OVERLAY_KEY, { type: 'journey', stages: next });
    setSaveState(ok ? 'saved' : 'idle');
  }, [editable, saveOverlay]);

  function rename(i: number, name: string) {
    setStages((prev) => prev.map((s, idx) => (idx === i ? { ...s, name } : s)));
  }
  // Rename persists on blur (not per keystroke) — one write when the consultant leaves the field.
  function commitRename() {
    setStages((prev) => { void persist(prev); return prev; });
  }
  function remove(i: number) {
    setStages((prev) => { const next = prev.filter((_, idx) => idx !== i); void persist(next); return next; });
  }
  function move(i: number, dir: -1 | 1) {
    setStages((prev) => {
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = prev.slice();
      [next[i], next[j]] = [next[j], next[i]];
      void persist(next);
      return next;
    });
  }
  function add() {
    setStages((prev) => { const next = [...prev, blankStage()]; void persist(next); return next; });
  }

  return (
    <div>
      <ProposalModal
        open={showModal}
        title="Proposed journey stages"
        subtitle="These stages were proposed from the corpus. Confirm them, or edit before the tab renders — a defined lifecycle (e.g. an 8-stage onboarding) should override a generic proposal before you present it."
        onUse={() => onConfirmProposal?.()}
        onEdit={() => { onConfirmProposal?.(); setEditMode(true); }}
      >
        <ol style={{ margin: 0, paddingLeft: 18 }}>
          {stages.map((s, i) => (
            <li key={i} style={{ fontSize: 13, color: color.text, lineHeight: 1.7 }}>
              <span style={{ fontWeight: 600, color: color.ink }}>{s.name}</span>
              <span style={{ fontSize: 11, fontFamily: mono, color: sentimentColor(s.sentiment), marginLeft: 8 }}>
                sentiment {Math.round(normalizeSentiment(s.sentiment) * 100)}%
              </span>
            </li>
          ))}
        </ol>
      </ProposalModal>

      {/* Header block */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: color.textFaint }}>TAB</span>
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', color: color.purple,
          background: color.pillActiveBg, padding: '2px 8px', borderRadius: 6,
        }}>USER JOURNEY</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: color.ink, letterSpacing: '-0.01em' }}>
            Stakeholder journey — stage by stage
          </h2>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: color.textMuted }}>
            Sentiment, goals, touchpoints and pain per stage. Scroll horizontally — stages never wrap.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {editable && saveState !== 'idle' && (
            <span style={{ fontSize: 12, color: saveState === 'saving' ? color.textMuted : color.green }}>
              {saveState === 'saving' ? 'Saving…' : 'Saved'}
            </span>
          )}
          <button
            onClick={async () => {
              if (exporting) return;
              setExporting(true);
              try { await exportJourneyPptx(stages, engagement, themes); }
              catch { /* user can retry */ }
              setExporting(false);
            }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer',
              padding: '8px 12px', borderRadius: 8, background: color.surface, color: color.text,
              border: `1px solid ${color.hairline}`,
            }}
          >
            <Download size={14} /> {exporting ? 'Exporting…' : 'Export to PowerPoint'}
          </button>
          <button
            onClick={() => setEditMode((v) => { const next = !v; if (v) void persist(stages); return next; })}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer',
              padding: '8px 12px', borderRadius: 8,
              background: editMode ? color.ink : color.surface, color: editMode ? '#fff' : color.text,
              border: `1px solid ${editMode ? 'transparent' : color.hairline}`,
            }}
          >
            <Pencil size={14} /> {editMode ? 'Done editing' : 'Edit stages'}
          </button>
        </div>
      </div>
      <div style={{ height: 1, background: color.hairline, margin: '18px 0' }} />

      {/* Inline stage editor */}
      {editMode && (
        <div style={{
          background: color.surfaceAlt, border: `1px solid ${color.hairline}`, borderRadius: 10,
          padding: '12px 14px', marginBottom: 16,
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: color.ink, marginBottom: 8 }}>
            Edit stages <span style={{ color: color.textFaint, fontWeight: 400 }}>· rename, reorder, add or remove</span>
          </div>
          {!editable && (
            <div style={{ fontSize: 11.5, color: '#B45309', marginBottom: 8 }}>
              Open a project to save these edits — changes here are preview-only.
            </div>
          )}
          {stages.map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <GripVertical size={14} color={color.textFaint} />
              <input
                value={s.name}
                onChange={(e) => rename(i, e.target.value)}
                onBlur={commitRename}
                style={{
                  flex: 1, fontSize: 13, padding: '6px 9px', borderRadius: 7,
                  border: `1px solid ${color.hairline}`, color: color.text, background: color.surface,
                }}
              />
              <button onClick={() => move(i, -1)} disabled={i === 0}
                style={{ fontSize: 12, padding: '5px 8px', borderRadius: 6, cursor: i === 0 ? 'default' : 'pointer',
                  background: color.surface, border: `1px solid ${color.hairline}`, color: color.textMuted }}>↑</button>
              <button onClick={() => move(i, 1)} disabled={i === stages.length - 1}
                style={{ fontSize: 12, padding: '5px 8px', borderRadius: 6, cursor: i === stages.length - 1 ? 'default' : 'pointer',
                  background: color.surface, border: `1px solid ${color.hairline}`, color: color.textMuted }}>↓</button>
              <X size={15} style={{ cursor: 'pointer', color: '#B91C1C' }} onClick={() => remove(i)} />
            </div>
          ))}
          <button onClick={add} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, marginTop: 6,
            padding: '6px 11px', borderRadius: 7, cursor: 'pointer',
            background: color.surface, border: `1px dashed ${color.separator}`, color: color.purple,
          }}>
            <Plus size={13} /> Add stage
          </button>
        </div>
      )}

      {/* Horizontal scroller — flex-nowrap so a 7-stage journey never wraps to a
          second row (README bug 2: wrapping silently misaligns the lanes). */}
      <div style={{ overflowX: 'auto', paddingBottom: 8 }}>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'nowrap', minWidth: 'min-content' }}>
          {stages.map((s, i) => (
            <StageCard
              key={i}
              s={s}
              open={!!openStages[i]}
              onToggle={() => setOpenStages((prev) => ({ ...prev, [i]: !prev[i] }))}
              onOpenTheme={(rank) => { setExpandedThemeRank(rank); setActive('key-themes'); }}
            />
          ))}
        </div>
      </div>

      {stages.length > 0 && <SentimentChart stages={stages} interviews={engagement.interviews} />}
      {stages.length > 0 && <JourneyInsights stages={stages} />}
    </div>
  );
}

function SentimentChart({ stages, interviews }: { stages: JourneyStage[]; interviews: number }) {
  const sent = stages.map((s) => normalizeSentiment(s.sentiment));
  let lowestIdx = 0;
  sent.forEach((v, i) => { if (v < sent[lowestIdx]) lowestIdx = i; });
  let drop: { delta: number; from: number; to: number } | null = null;
  for (let i = 1; i < sent.length; i++) {
    const delta = sent[i - 1] - sent[i];
    if (!drop || delta > drop.delta) drop = { delta, from: i - 1, to: i };
  }
  const W = 960, H = 320, mL = 70, mR = 40, mT = 40, mB = 80;
  const cW = W - mL - mR, cH = H - mT - mB;
  const stageX = (i: number) => stages.length === 1 ? mL + cW / 2 : mL + (i / (stages.length - 1)) * cW;
  const sentY = (v: number) => mT + (1 - Math.max(0, Math.min(1, v))) * cH;
  const bandMid1 = sentY(0.66);
  const bandMid2 = sentY(0.33);
  const bandBot = mT + cH;
  let pathD = '';
  stages.forEach((_, i) => {
    const x = stageX(i), y = sentY(sent[i]);
    pathD = i === 0 ? `M ${x} ${y}` : `${pathD} C ${(stageX(i - 1) + x) / 2} ${sentY(sent[i - 1])}, ${(stageX(i - 1) + x) / 2} ${y}, ${x} ${y}`;
  });
  const areaD = `${pathD} L ${stageX(stages.length - 1)} ${bandBot} L ${stageX(0)} ${bandBot} Z`;
  const totalQuotes = stages.reduce((a, s) => a + (s.quotes || 0), 0);
  return (
    <div style={{ marginTop: 28 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: color.ink }}>Sentiment curve</h3>
          <div style={{ fontSize: 12, color: color.textMuted, marginTop: 2 }}>
            aggregated from {countOf(totalQuotes, 'tagged quote')}{interviews ? ` across ${countOf(interviews, 'interview')}` : ''} · 0 = negative, 1 = positive
          </div>
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxWidth: W, display: 'block' }}>
        <rect x={mL} y={mT} width={cW} height={bandMid1 - mT} fill="#DCFCE7" opacity={0.35} />
        <rect x={mL} y={bandMid1} width={cW} height={bandMid2 - bandMid1} fill="#F1F5F9" opacity={0.5} />
        <rect x={mL} y={bandMid2} width={cW} height={bandBot - bandMid2} fill="#FEE2E2" opacity={0.35} />
        <line x1={mL} x2={mL + cW} y1={bandMid1} y2={bandMid1} stroke="#E2E8F0" strokeDasharray="4 4" />
        <line x1={mL} x2={mL + cW} y1={bandMid2} y2={bandMid2} stroke="#E2E8F0" strokeDasharray="4 4" />
        <text x={mL - 8} y={sentY(0.85) + 3} fontSize={9} fontWeight={700} textAnchor="end" fill="#10B981">POSITIVE</text>
        <text x={mL - 8} y={sentY(0.50) + 3} fontSize={9} fontWeight={700} textAnchor="end" fill="#64748B">NEUTRAL</text>
        <text x={mL - 8} y={sentY(0.15) + 3} fontSize={9} fontWeight={700} textAnchor="end" fill="#EF4444">NEGATIVE</text>
        <path d={areaD} fill="rgba(124,58,237,0.06)" />
        <path d={pathD} stroke="#7C3AED" strokeWidth={2.5} fill="none" strokeLinecap="round" />
        {stages.map((s, i) => {
          const x = stageX(i), y = sentY(sent[i]);
          const isLow = i === lowestIdx;
          return (
            <g key={i}>
              <circle cx={x} cy={y} r={isLow ? 8 : 6} fill={isLow ? '#EF4444' : (s.color || '#7C3AED')} stroke="#FFF" strokeWidth={2.5} />
              <rect x={x - 20} y={y - 26} width={40} height={18} rx={4} fill="#FFF" stroke="#E2E8F0" />
              <text x={x} y={y - 13} fontSize={11} fontWeight={600} textAnchor="middle" fill={isLow ? '#B91C1C' : '#334155'}>{sent[i].toFixed(2)}</text>
              <text x={x} y={bandBot + 22} fontSize={11} fontWeight={600} textAnchor="middle" fill="#0F172A">
                {(s.name || '').length > 16 ? `${s.name.slice(0, 15)}…` : s.name}
              </text>
            </g>
          );
        })}
        {drop && drop.delta > 0.05 && stages.length > 1 && (
          <g>
            <rect x={Math.min(stageX(drop.from), stageX(drop.to))} y={mT - 8} width={150} height={34} rx={4} fill="#FFF" stroke="#FCA5A5" />
            <text x={Math.min(stageX(drop.from), stageX(drop.to)) + 10} y={mT + 6} fontSize={10} fontWeight={700} fill="#B91C1C">
              −{Math.round(drop.delta * 100)}pts DROP
            </text>
            <text x={Math.min(stageX(drop.from), stageX(drop.to)) + 10} y={mT + 20} fontSize={9} fill="#64748B">
              {stages[drop.from].name} → {stages[drop.to].name}
            </text>
          </g>
        )}
      </svg>
    </div>
  );
}

function JourneyInsights({ stages }: { stages: JourneyStage[] }) {
  const sent = stages.map((s) => normalizeSentiment(s.sentiment));
  let lowestIdx = 0;
  sent.forEach((v, i) => { if (v < sent[lowestIdx]) lowestIdx = i; });
  let drop: { delta: number; from: number; to: number } | null = null;
  for (let i = 1; i < sent.length; i++) {
    const delta = sent[i - 1] - sent[i];
    if (!drop || delta > drop.delta) drop = { delta, from: i - 1, to: i };
  }
  const first = sent[0] || 0;
  const last = sent[sent.length - 1] || 0;
  const recovery = last - sent[lowestIdx];
  const gapVsStart = first - last;
  const card = (kicker: string, title: string, body: string, hot?: boolean) => (
    <div style={{
      flex: 1, minWidth: 200, border: `1px solid ${hot ? '#FCA5A5' : color.hairline}`,
      background: hot ? '#FEF2F2' : color.surface, borderRadius: 10, padding: '12px 14px',
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: hot ? '#B91C1C' : color.textFaint }}>{kicker}</div>
      <div style={{ marginTop: 4, fontSize: 14, fontWeight: 700, color: color.ink }}>{title}</div>
      <div style={{ marginTop: 6, fontSize: 12, color: color.textMuted, lineHeight: 1.5 }}>{body}</div>
    </div>
  );
  return (
    <div style={{ marginTop: 22 }}>
      <h3 style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 700, color: color.ink }}>Journey Insights</h3>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {card('LOWEST POINT', stages[lowestIdx]?.name || '—',
          `Sentiment ${sent[lowestIdx].toFixed(2)} (${Math.round(sent[lowestIdx] * 100)}%). This is the trough of the arc.`, true)}
        {drop && drop.delta > 0.05
          ? card('BIGGEST DROP', `${stages[drop.from]?.name || '—'} → ${stages[drop.to]?.name || '—'}`,
            `${Math.round(drop.delta * 100)}pt sentiment fall. From ${sent[drop.from].toFixed(2)} to ${sent[drop.to].toFixed(2)}. This is where the confidence gap opens.`)
          : card('STABLE ARC', 'No sharp drop', 'Sentiment holds across stages — no single handoff opens a confidence gap.')}
        {card('RECOVERY GAP', recovery >= 0 ? `+${Math.round(recovery * 100)}pts from trough` : `${Math.round(recovery * 100)}pts from trough`,
          `Finish vs start: ${gapVsStart >= 0 ? 'ends below the opening' : 'recovers past the opening'} (${first.toFixed(2)} → ${last.toFixed(2)}).`)}
      </div>
    </div>
  );
}
